/**
 * Dashboard Bootstrap Cache (iter 251).
 *
 * Collapses the 20+ dashboard GETs on login into one server round-trip.
 *
 * How it works:
 *   1. On login success (or explicit `preloadDashboard()` call), we fetch
 *      `/api/dashboard/bootstrap` — which returns all dashboard data in
 *      one parallel burst on the server.
 *   2. We seed this module's `cache` map, keyed by the URL the frontend
 *      WOULD have called (e.g. `/api/service-calls?page=1&page_size=50`).
 *   3. The axios adapter in `networkMiddleware.js` checks this cache
 *      BEFORE hitting the network. Cache hit → instant synthetic response.
 *      Cache miss → normal network call.
 *   4. Entries expire after DEFAULT_TTL_MS to avoid stale data. The next
 *      GET after expiry hits the network normally, which will populate
 *      SWR/direct-axios caches for ongoing use.
 *
 * This lets us solve the thundering-herd problem WITHOUT touching any of
 * the 20 components that fire the GETs. They all run unchanged; they just
 * hit a cache on first render instead of the network.
 */

import BACKEND_URL from "./apiConfig";

// Map of `cache-key` → `{ data, expiresAt }`
const cache = new Map();

// In-flight bootstrap promise — set while a `preloadDashboard()` call
// is mid-flight, cleared on settle. Lets multiple call-sites dedupe
// onto a single round-trip (iter 370cn — login page + App.js both
// race to fire the preload; they should share one network call).
let inflight = null;

// How long a cached entry survives before falling back to the network.
// Short enough that users don't see stale data; long enough to cover the
// entire dashboard-mount phase where 20 components each fetch their slice.
const DEFAULT_TTL_MS = 30_000;

// Maps bootstrap-response key → URL that the frontend fetches naturally.
// When the frontend fires `axios.get('/api/customers')`, the adapter
// normalizes it to the same key so it hits the cache.
const BOOTSTRAP_KEY_TO_URL = {
  service_calls:                "/api/service-calls?page=1&page_size=50",
  service_calls_pinned:         "/api/service-calls/pinned",
  service_calls_attention:      "/api/service-calls/attention",
  service_calls_unscheduled:    "/api/service-calls/unscheduled-goals",
  service_calls_kpi:            "/api/service-calls/kpi-current",
  customers:                    "/api/customers",
  users:                        "/api/users",
  parts:                        "/api/parts",
  tags:                         "/api/tags",
  knowledge:                    "/api/knowledge",
  delivery_drivers:             "/api/delivery-drivers",
  deliveries_as_calls:          "/api/deliveries/as-calls",
  deliveries_color_options:     "/api/deliveries/color-options",
  deliveries_color_mismatches:  "/api/deliveries/color-mismatches",
  maintenance_alerts:           "/api/maintenance-alerts",
  bulletins_active_count:       "/api/bulletins/active-count",
  analytics_average_age:        "/api/analytics/average-age",
  airtable_sync_status:         "/api/integrations/airtable/sync-status",
};

/** Normalize a URL for cache lookup. Strips the backend origin and any
 *  trailing slash. `?page=1&page_size=50` is preserved because it's part
 *  of the cache key. */
export function normalizeUrl(url) {
  if (!url) return url;
  let u = url;
  // Strip the origin so both absolute and relative URLs match
  if (BACKEND_URL && u.startsWith(BACKEND_URL)) u = u.slice(BACKEND_URL.length);
  // Strip trailing slash (but not the root)
  if (u.length > 1 && u.endsWith("/")) u = u.slice(0, -1);
  return u;
}

/** Fetch the dashboard bootstrap and seed the cache.
 *  Returns `true` on success, `false` on failure — failure is non-fatal;
 *  the app continues to work, just without the cache speedup.
 *
 *  iter 370cn — guards against double-fire. If a preload is already in
 *  flight (e.g. PinLogin kicked one off before App.js handleLogin did),
 *  subsequent callers join the same promise instead of hitting the
 *  endpoint twice. */
export async function preloadDashboard(fetchFn, { ttlMs = DEFAULT_TTL_MS, dates } = {}) {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      return await _doPreload(fetchFn, { ttlMs, dates });
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

async function _doPreload(fetchFn, { ttlMs, dates }) {
  const url = dates
    ? `${BACKEND_URL}/api/dashboard/bootstrap?dates=${encodeURIComponent(dates)}`
    : `${BACKEND_URL}/api/dashboard/bootstrap`;

  try {
    // Use the caller-provided fetch (e.g. authenticated axios) so cookies
    // and auth headers are included. Fall back to window.fetch.
    const resp = fetchFn
      ? await fetchFn(url, { method: "GET" })
      : await fetch(url, { method: "GET", credentials: "include" });

    // axios-style response has `.data`; fetch-style has `.json()`
    const data =
      resp && typeof resp.json === "function"
        ? await resp.json()
        : resp?.data ?? resp;

    if (!data || typeof data !== "object") {
      console.warn("[dashboardBootstrap] unexpected payload shape");
      return false;
    }

    const expiresAt = Date.now() + ttlMs;
    let seeded = 0;
    for (const [key, frontendUrl] of Object.entries(BOOTSTRAP_KEY_TO_URL)) {
      const entry = data[key];
      if (entry !== undefined && entry !== null) {
        cache.set(normalizeUrl(frontendUrl), { data: entry, expiresAt });
        seeded++;
      }
    }

    // Log bootstrap perf so ops can spot regressions
    const meta = data._meta || {};
    const errors = data._errors || {};
    console.info(
      `[dashboardBootstrap] seeded ${seeded}/${Object.keys(BOOTSTRAP_KEY_TO_URL).length} keys ` +
      `in ${meta.duration_ms || "?"}ms (${Object.keys(errors).length} section errors)`
    );
    return true;
  } catch (err) {
    // Non-fatal — 20 individual GETs will handle themselves
    console.warn(`[dashboardBootstrap] preload failed (non-fatal): ${err?.message}`);
    return false;
  }
}

/** Look up a cached entry. Returns the data or `undefined` if not cached
 *  / expired. Called from the axios adapter on every GET request. */
export function getCachedResponse(url) {
  const key = normalizeUrl(url);
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (entry.expiresAt < Date.now()) {
    cache.delete(key);
    return undefined;
  }
  return entry.data;
}

/** Invalidate one entry (after a mutation) or everything (`undefined`). */
export function invalidateBootstrapCache(url) {
  if (url === undefined) {
    cache.clear();
    return;
  }
  cache.delete(normalizeUrl(url));
}

/** For tests & diagnostics. */
export function bootstrapCacheSize() {
  return cache.size;
}
